import { Button, Popconfirm, Tag } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { createUseStyles } from 'react-jss'
import { isCurrentCycle } from '../../lib/cycles'
import { formatDateTime } from '../../lib/datetime'
import { DatePicker } from '../Forms'
import { cycleShape } from '../prop-types'

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  // One block per sub-period: the "Checks" tag on the left, its dates on the
  // right, so a long-period cycle reads as a single grouped card.
  period: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 2,
  },
  periodBody: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  range: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  editField: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  // The Formik DatePicker renders inside a Form.Item, whose bottom margin would
  // leave a large gap between the stacked edit fields on the card.
  datePicker: {
    marginBottom: '0 !important',
  },
  actionsLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  tag: {
    marginInlineEnd: 0,
  },
})

// dayjs(undefined) formats as today, so never format a missing date.
function formatDate(date) {
  return date ? formatDateTime(date) : '—'
}

// The desktop table splits a long-period cycle into two rows sharing the period
// number and the edit action. On mobile both live in the same card as one block
// each, tagged exactly like the desktop "Checks" column.
function subPeriodsFor(cycle) {
  const shortPeriod = {
    key: 'short',
    label: '30 days',
    color: 'cyan',
    startsAt: cycle.startsAt,
    endsAt: cycle.endsAt,
    reportDate: cycle.reportDate,
  }
  if (!cycle.longPeriod) return [shortPeriod]
  return [
    shortPeriod,
    {
      key: 'long',
      label: '90 days',
      color: 'purple',
      startsAt: cycle.startsAtLong,
      endsAt: cycle.endsAtLong,
      reportDate: cycle.reportDateLong,
    },
  ]
}

function CycleSubPeriod({ period, editing = false, canEditStart = false }) {
  const classes = useStyles()
  return (
    <div className={classes.period}>
      <Tag className={classes.tag} color={period.color}>
        {period.label}
      </Tag>
      <div className={classes.periodBody}>
        {editing ? (
          <>
            <div className={classes.editField}>
              <span className={classes.fieldLabel}>Start</span>
              {canEditStart ? (
                <DatePicker
                  id="startsAt"
                  name="startsAt"
                  allowClear={false}
                  containerClassName={classes.datePicker}
                />
              ) : (
                <span className={classes.range}>
                  {formatDate(period.startsAt)}
                </span>
              )}
            </div>
            <div className={classes.editField}>
              <span className={classes.fieldLabel}>Ends</span>
              <DatePicker
                id="endsAt"
                name="endsAt"
                allowClear={false}
                containerClassName={classes.datePicker}
              />
            </div>
          </>
        ) : (
          <span className={classes.range}>
            {formatDate(period.startsAt)} – {formatDate(period.endsAt)}
          </span>
        )}
        <span className={classes.line}>
          Reports: {formatDate(period.reportDate)}
        </span>
      </div>
    </div>
  )
}

CycleSubPeriod.propTypes = {
  period: PropTypes.shape({
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    startsAt: PropTypes.string,
    endsAt: PropTypes.string,
    reportDate: PropTypes.string,
  }).isRequired,
  editing: PropTypes.bool,
  canEditStart: PropTypes.bool,
}

export default function CycleMobileCard({
  cycle,
  periodNumber,
  editing = false,
  canEditStart = false,
  editDisabled = false,
  onEditCycle,
  onSubmit = undefined,
}) {
  const classes = useStyles()
  const subPeriods = subPeriodsFor(cycle)

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <span className={classes.name}>Period {periodNumber}</span>
        {isCurrentCycle(cycle) && (
          <Tag className={classes.tag} color="green">
            current
          </Tag>
        )}
      </div>
      <div className={classes.line}>
        Remote authorizations: {formatDate(cycle.preAuthorizationDate)}
      </div>
      {subPeriods.map(period => (
        <CycleSubPeriod
          key={period.key}
          period={period}
          // Only the 30-day period is editable; the 90-day dates are derived.
          editing={editing && period.key === 'short'}
          canEditStart={canEditStart}
        />
      ))}
      {onSubmit && (
        <div className={classes.actionsLine}>
          {editing ? (
            <>
              <Popconfirm
                title="This will update the dates for all subsequent cycles. Are you sure?"
                okText="Yes"
                cancelText="No"
                onConfirm={onSubmit}
                placement="topRight"
              >
                <Button type="link" size="small" style={{ paddingLeft: 0 }}>
                  Save
                </Button>
              </Popconfirm>
              <Button
                type="link"
                size="small"
                onClick={() => onEditCycle(null)}
                style={{ paddingLeft: 0 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEditCycle(cycle.id)}
              disabled={editDisabled}
              style={{ paddingLeft: 0 }}
            >
              Edit
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

CycleMobileCard.propTypes = {
  cycle: cycleShape.isRequired,
  periodNumber: PropTypes.number.isRequired,
  editing: PropTypes.bool,
  canEditStart: PropTypes.bool,
  editDisabled: PropTypes.bool,
  onEditCycle: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
}
